import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { CrosshairIcon } from "@phosphor-icons/react/dist/csr/Crosshair";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { ApiError } from "@/core/api/types";
import { formatPersistentEditValue } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import type { IAiSpreadsheetCellLocation } from "@/payroll/ai-script/types";

export const showPersistentEditMutationError = (
  message: string,
  error: unknown,
): boolean => {
  if (error instanceof ApiError && error.status === 403) {
    toast.error("You don't have permission to review this edit", {
      description:
        "Your Grid access may have changed. The available edits are being refreshed.",
    });
    return true;
  }

  toast.error(message, {
    description: "Something went wrong. Try again.",
  });
  return false;
};

export const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Text variant="title">{children}</Text>
);

export const EditCard = ({
  children,
  onClick,
  className,
  ...props
}: ComponentProps<"div">) => (
  <div
    className={cn(
      "w-full min-w-0 overflow-hidden rounded-xl bg-neutral p-4",
      {
        "cursor-pointer transition-colors outline-none hover:bg-neutral-subtle-hovered focus-visible:ring-3 focus-visible:ring-focus/50":
          !!onClick,
      },
      className,
    )}
    onClick={onClick}
    // Holds action buttons, so not a <button>; Enter/Space use onKeyDown.
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.currentTarget.click();
          }
        : undefined
    }
    {...props}
  >
    <Stack gap={2} align="stretch" className="w-full min-w-0">
      {children}
    </Stack>
  </div>
);

const hasValue = (value: string | number | boolean | null | undefined) =>
  value !== null && value !== undefined && value !== "";

// An edit is a change, so show it as one — "34.25 → 34" says more than either
// number alone. Cells that were blank before the edit have nothing to compare
// against, so they just show what they hold now.
export const EditValueChange = ({
  from,
  to,
}: {
  from: string | number | boolean | null | undefined;
  to: string | number | boolean | null | undefined;
}) => (
  <Stack direction="row" gap={2} align="center" className="w-full min-w-0">
    {hasValue(from) ? (
      <>
        <Text
          tone="muted"
          className="max-w-[45%] min-w-0 shrink-0 truncate"
          title={formatPersistentEditValue(from)}
        >
          {formatPersistentEditValue(from)}
        </Text>
        <ArrowRightIcon className="h-4 w-4 shrink-0 text-subtle" />
      </>
    ) : null}
    <Text className="min-w-0 flex-1 font-semibold">
      <PersistentEditValue value={to} />
    </Text>
  </Stack>
);

export const BoldValue = ({ children }: { children: ReactNode }) => (
  <span className="font-semibold text-ink">{children}</span>
);

export const PersistentEditValue = ({
  value,
}: {
  value: string | number | boolean | null | undefined;
}) => {
  const displayValue = formatPersistentEditValue(value);

  return (
    <Tooltip>
      {/* inline-block so the value can sit inside a sentence ("Your edit (12)
          matched…") as well as stand alone as a card's headline. */}
      <TooltipTrigger
        render={
          <span
            className="inline-block max-w-full min-w-0 truncate align-bottom font-semibold text-ink"
            data-persistent-edit-value
          />
        }
      >
        {displayValue}
      </TooltipTrigger>
      <TooltipContent className="max-w-sm wrap-anywhere">
        {displayValue}
      </TooltipContent>
    </Tooltip>
  );
};

export const CellLocationLink = ({
  cell,
  onGoToCell,
}: {
  cell: IAiSpreadsheetCellLocation | null | undefined;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
}) => {
  if (!cell) return null;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              onGoToCell(cell);
            }}
            className="-ml-2 min-w-0 shrink font-semibold"
          />
        }
      >
        <CrosshairIcon data-icon="inline-start" className="text-subtle" />
        <span
          className="truncate"
          title={
            cell.sheet_name
              ? `${cell.sheet_name} · ${cell.address}`
              : cell.address
          }
        >
          {cell.sheet_name
            ? `${cell.sheet_name} · ${cell.address}`
            : cell.address}
        </span>
      </TooltipTrigger>
      <TooltipContent>Go to cell</TooltipContent>
    </Tooltip>
  );
};

export const LoadError = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <Stack className="w-full" gap={2} align="start">
    <Text variant="caption" tone="muted">
      {message}
    </Text>
    <Button variant="secondary" size="sm" onClick={onRetry}>
      Try again
    </Button>
  </Stack>
);
